import { CalendarDays, CheckCircle2, FileText, Mail, MapPin, Phone, ShieldCheck, Sparkles } from "lucide-react";

const featureCards = [
  {
    icon: Sparkles,
    title: "Free financial evaluation",
    text: "A clear review of your current protection, goals, and options—before you make any commitments.",
    className: "md:col-span-5 bg-white",
    testId: "feature-free-evaluation-card",
  },
  {
    icon: ShieldCheck,
    title: "Insurance made understandable",
    text: "Straight answers around coverage, retirement income, family protection, and long-term planning.",
    className: "md:col-span-4 bg-[#EAE6DF]",
    testId: "feature-insurance-clarity-card",
  },
  {
    icon: CheckCircle2,
    title: "Low-pressure environment",
    text: "Move at your pace. Ask questions freely. Leave with practical next steps—not pressure.",
    className: "md:col-span-3 bg-[#C5CEC4]/55",
    testId: "feature-low-pressure-card",
  },
];

const DEFAULT_HEADSHOT_URL = "https://customer-assets.emergentagent.com/job_aisha-low-pressure/artifacts/fnen94b2_headshot2.jpg";

const getSafeHeadshotUrl = (url) => {
  if (!url || url.includes("mail.google.com") || url.toLowerCase().includes("placeholder")) {
    return DEFAULT_HEADSHOT_URL;
  }
  return url;
};

export const LandingPage = ({ content }) => {
  const contactAnchor = "#contact";
  const headshotUrl = getSafeHeadshotUrl(content.headshot_url);

  return (
    <main className="min-h-screen overflow-hidden bg-[#FAFAF9] text-[#252E2B]" data-testid="landing-page">
      <div className="pointer-events-none fixed inset-0 -z-10 opacity-80" aria-hidden="true">
        <div className="absolute left-[-8rem] top-[-8rem] h-96 w-96 rounded-full bg-[#C5CEC4]/60 blur-3xl" />
        <div className="absolute bottom-20 right-[-6rem] h-[28rem] w-[28rem] rounded-full bg-[#EAE6DF] blur-3xl" />
      </div>

      <header className="sticky top-0 z-40 border-b border-[#252E2B]/10 bg-[#FAFAF9]/80 backdrop-blur-xl" data-testid="site-header">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8" aria-label="Main navigation">
          <a href="#top" className="group" data-testid="header-logo-link">
            <span className="block font-heading text-2xl leading-none text-[#252E2B]" data-testid="header-agent-name">
              {content.name}
            </span>
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#5C6A64]" data-testid="header-agent-role">
              Insurance Advisor
            </span>
          </a>
          <div className="hidden items-center gap-8 font-body text-sm text-[#5C6A64] md:flex" data-testid="header-navigation-links">
            <a className="transition-colors duration-300 hover:text-[#9E4D3E]" href="#evaluation" data-testid="nav-evaluation-link">
              Free Evaluation
            </a>
            <a className="transition-colors duration-300 hover:text-[#9E4D3E]" href="#booking" data-testid="nav-booking-link">
              Booking
            </a>
            <a className="transition-colors duration-300 hover:text-[#9E4D3E]" href={contactAnchor} data-testid="nav-contact-link">
              Contact
            </a>
          </div>
          <div className="flex items-center gap-2">
            <a
              className="rounded-full bg-[#9E4D3E] px-5 py-3 font-body text-sm font-medium text-[#FAFAF9] shadow-lg shadow-[#9E4D3E]/20 transition-transform duration-300 hover:-translate-y-1 hover:bg-[#833E31]"
              href={content.calendly_url}
              target="_blank"
              rel="noreferrer"
              data-testid="header-calendly-booking-button"
            >
              Book Now
            </a>
          </div>
        </nav>
      </header>

      <section id="top" className="relative mx-auto grid max-w-7xl gap-12 px-5 py-16 md:grid-cols-[1.05fr_0.95fr] md:px-8 md:py-24 lg:py-28" data-testid="hero-section">
        <div className="flex flex-col justify-center" data-testid="hero-copy-block">
          <div className="mb-6" data-testid="hero-rise-block">
            <p className="font-heading text-4xl tracking-[0.08em] text-[#252E2B] sm:text-5xl" data-testid="hero-rise-acronym">
              R.I.S.E.
            </p>
            <p className="mt-1 font-body text-[11px] uppercase tracking-[0.14em] text-[#252E2B] sm:text-xs" data-testid="hero-rise-expansion">
              Realestate. Insurance. Stocks. Entrepeneurship
            </p>
          </div>
          <p className="mb-5 font-body text-xs uppercase tracking-[0.2em] text-[#9E4D3E]" data-testid="hero-eyebrow">
            {content.eyebrow}
          </p>
          <h1 className="max-w-4xl font-heading text-5xl leading-[1.05] tracking-tight text-[#252E2B] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {content.headline}
          </h1>
          <p className="mt-7 max-w-2xl font-body text-base leading-relaxed text-[#5C6A64] md:text-lg" data-testid="hero-subtitle">
            {content.subtitle}
          </p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap" data-testid="hero-cta-group">
            <a
              className="rounded-full bg-[#9E4D3E] px-7 py-4 text-center font-body text-sm font-semibold uppercase tracking-[0.12em] text-[#FAFAF9] shadow-xl shadow-[#9E4D3E]/20 transition-transform duration-300 hover:-translate-y-1 hover:bg-[#833E31]"
              href={content.calendly_url}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-calendly-booking-button"
            >
              {content.primary_cta}
            </a>
            <a
              className="rounded-full bg-[#252E2B] px-7 py-4 text-center font-body text-sm font-semibold uppercase tracking-[0.12em] text-[#FAFAF9] shadow-xl shadow-[#252E2B]/15 transition-transform duration-300 hover:-translate-y-1 hover:bg-[#4A5D54]"
              href={content.ethos_quote_url}
              data-testid="hero-ethos-quote-button"
            >
              {content.quote_cta}
            </a>
            <a
              className="rounded-full border border-[#4A5D54]/25 bg-[#C5CEC4]/45 px-7 py-4 text-center font-body text-sm font-semibold uppercase tracking-[0.12em] text-[#252E2B] shadow-lg shadow-[#252E2B]/5 transition-transform duration-300 hover:-translate-y-1 hover:bg-[#C5CEC4]/75"
              href={content.join_team_url}
              data-testid="hero-join-team-button"
            >
              {content.join_team_cta}
            </a>
            <a
              className="rounded-full border border-[#252E2B]/15 bg-white/60 px-7 py-4 text-center font-body text-sm font-semibold uppercase tracking-[0.12em] text-[#252E2B] transition-transform duration-300 hover:-translate-y-1 hover:bg-white"
              href={contactAnchor}
              data-testid="hero-contact-link-button"
            >
              {content.secondary_cta}
            </a>
          </div>
        </div>

        <div className="relative" data-testid="hero-headshot-card">
          <div className="relative overflow-hidden rounded-[2rem] border border-[#252E2B]/10 bg-[#EAE6DF] p-4 shadow-2xl shadow-[#252E2B]/10">
            <div className="aspect-[4/5] overflow-hidden rounded-[1.5rem] bg-[#C5CEC4]" data-testid="headshot-image-frame">
              <img
                src={headshotUrl}
                alt={`${content.name} professional headshot`}
                className="h-full w-full object-cover object-center transition-transform duration-700 hover:scale-105"
                onError={(event) => {
                  event.currentTarget.src = DEFAULT_HEADSHOT_URL;
                }}
                data-testid="headshot-image"
              />
            </div>
            <div className="mt-4 rounded-3xl border border-white/60 bg-[#FAFAF9]/90 p-5 shadow-xl backdrop-blur-xl" data-testid="headshot-short-bio-card">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-[#9E4D3E]" data-testid="headshot-bio-label">
                About Aisha
              </p>
              <p className="mt-2 font-body text-sm leading-relaxed text-[#252E2B] md:text-base" data-testid="headshot-short-bio-text">
                {content.short_bio}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="evaluation" className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24" data-testid="evaluation-section">
        <div className="mb-10 max-w-3xl" data-testid="evaluation-heading-block">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-[#9E4D3E]" data-testid="evaluation-eyebrow">
            What to expect
          </p>
          <h2 className="mt-3 font-heading text-4xl tracking-tight text-[#252E2B] md:text-5xl" data-testid="evaluation-title">
            A thoughtful evaluation built around your comfort level.
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-12" data-testid="feature-card-grid">
          {featureCards.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <article
                key={feature.title}
                className={`${feature.className} reveal-card rounded-[2rem] border border-[#252E2B]/10 p-8 shadow-sm transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl md:p-10`}
                style={{ animationDelay: `${index * 120}ms` }}
                data-testid={feature.testId}
              >
                <Icon className="mb-8 h-9 w-9 text-[#9E4D3E]" strokeWidth={1.5} data-testid={`${feature.testId}-icon`} />
                <h3 className="font-heading text-2xl text-[#252E2B]" data-testid={`${feature.testId}-title`}>
                  {feature.title}
                </h3>
                <p className="mt-4 font-body text-base leading-relaxed text-[#5C6A64]" data-testid={`${feature.testId}-text`}>
                  {feature.text}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section id="booking" className="relative my-8 bg-[#252E2B] px-5 py-20 text-[#FAFAF9] md:my-16 md:px-8 md:py-28" data-testid="booking-section">
        <div className="absolute inset-0 opacity-20" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1604845152150-79d128ac8817?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBwbGFudCUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4MDYwNDQzN3ww&ixlib=rb-4.1.0&q=85"
            alt="Warm professional office background"
            className="h-full w-full object-cover"
            data-testid="booking-background-image"
          />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-10 md:grid-cols-[0.9fr_1.1fr] md:items-center" data-testid="booking-content-grid">
          <div data-testid="booking-copy-block">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-[#E7B8A9]" data-testid="booking-eyebrow">
              Free financial evaluation
            </p>
            <h2 className="mt-4 font-heading text-4xl tracking-tight md:text-6xl" data-testid="booking-title">
              Book a calm, useful conversation.
            </h2>
          </div>
          <div className="rounded-[2rem] border border-white/10 bg-[#FAFAF9]/10 p-8 backdrop-blur-md md:p-10" data-testid="booking-card">
            <CalendarDays className="mb-8 h-10 w-10 text-[#E7B8A9]" strokeWidth={1.5} data-testid="booking-card-icon" />
            <p className="font-body text-lg leading-relaxed text-[#FAFAF9]/85" data-testid="booking-description">
              Choose a time that works for you. This introductory evaluation is designed to help you understand your options with no obligation.
            </p>
            <a
              className="mt-8 inline-flex rounded-full bg-[#9E4D3E] px-7 py-4 font-body text-sm font-semibold uppercase tracking-[0.12em] text-[#FAFAF9] shadow-xl shadow-black/20 transition-transform duration-300 hover:-translate-y-1 hover:bg-[#833E31]"
              href={content.calendly_url}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-calendly-button"
            >
              {content.primary_cta}
            </a>
            <a
              className="ml-0 mt-4 inline-flex rounded-full border border-[#FAFAF9]/20 bg-[#FAFAF9]/10 px-7 py-4 font-body text-sm font-semibold uppercase tracking-[0.12em] text-[#FAFAF9] transition-transform duration-300 hover:-translate-y-1 hover:bg-[#FAFAF9]/20 sm:ml-3"
              href={content.ethos_quote_url}
              data-testid="booking-ethos-quote-button"
            >
              <FileText className="mr-2 h-4 w-4" strokeWidth={1.5} /> {content.quote_cta}
            </a>
          </div>
        </div>
      </section>

      <footer id="contact" className="bg-[#EAE6DF] px-5 py-16 md:px-8 md:py-20" data-testid="contact-footer">
        <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-[0.9fr_1.1fr]" data-testid="contact-footer-grid">
          <div data-testid="contact-footer-heading-block">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-[#9E4D3E]" data-testid="contact-footer-eyebrow">
              Contact information
            </p>
            <h2 className="mt-4 font-heading text-4xl text-[#252E2B] md:text-5xl" data-testid="contact-footer-title">
              {content.footer_note}
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-3" data-testid="contact-card-grid">
            <a className="rounded-[1.5rem] bg-[#FAFAF9] p-6 transition-transform duration-300 hover:-translate-y-1" href={`tel:${content.phone}`} data-testid="contact-phone-link">
              <Phone className="mb-5 h-6 w-6 text-[#9E4D3E]" strokeWidth={1.5} data-testid="contact-phone-icon" />
              <p className="font-body text-xs uppercase tracking-[0.18em] text-[#5C6A64]" data-testid="contact-phone-label">Phone</p>
              <p className="mt-2 break-words font-body text-lg text-[#252E2B]" data-testid="contact-phone-value">{content.phone}</p>
            </a>
            <a className="rounded-[1.5rem] bg-[#FAFAF9] p-6 transition-transform duration-300 hover:-translate-y-1" href={`mailto:${content.email}`} data-testid="contact-email-link">
              <Mail className="mb-5 h-6 w-6 text-[#9E4D3E]" strokeWidth={1.5} data-testid="contact-email-icon" />
              <p className="font-body text-xs uppercase tracking-[0.18em] text-[#5C6A64]" data-testid="contact-email-label">Email</p>
              <p className="mt-2 break-words font-body text-lg text-[#252E2B]" data-testid="contact-email-value">{content.email}</p>
            </a>
            <div className="rounded-[1.5rem] bg-[#FAFAF9] p-6" data-testid="contact-location-card">
              <MapPin className="mb-5 h-6 w-6 text-[#9E4D3E]" strokeWidth={1.5} data-testid="contact-location-icon" />
              <p className="font-body text-xs uppercase tracking-[0.18em] text-[#5C6A64]" data-testid="contact-location-label">Location</p>
              <p className="mt-2 break-words font-body text-lg text-[#252E2B]" data-testid="contact-location-value">{content.location}</p>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
};